/**
 * Which document types are in play, and the texts chosen by them (docs/review/doc-type-audit.md; Matt, 2026-10-18,
 * docs/specs/morning-2026-10-18.md part 7). A shared line says what was actually loaded, not only what the start screen
 * asked for: a Form 106 added to a payslip check, or a December payslip added to a Form 106 check, is named as itself.
 * Pure, so each rule is tested on its own (docTypes.test.ts).
 */

export type Goal = 'month' | 'several' | 'left' | 'form106';

/** What was loaded: payslips read (from files, typed or pasted) and Form 106s (read or typed). */
export interface Loaded {
  payslips: number;
  forms106: number;
}

/**
 * The step summary's "Checking: …" line. Only Form 106s loaded → the Form 106 check; "Translate a payslip" → that;
 * one payslip goal with several payslips → "Several payslips"; a Form 106 goal with only payslips → the payslip goal.
 */
export function goalSummaryKey(goal: Goal, loaded: Loaded, translate: boolean): string {
  if (loaded.forms106 > 0 && loaded.payslips === 0) return 'flow.goal.form106';
  if (translate) return 'brand.translateInto';
  if (goal === 'form106' && loaded.payslips > 0 && loaded.forms106 === 0) return loaded.payslips > 1 ? 'flow.goal.several' : 'flow.goal.month';
  if (goal === 'month' && loaded.payslips > 1) return 'flow.goal.several';
  return `flow.goal.${goal}`;
}

/**
 * The documents line's label ("Payslips" / "106 forms") on the step summary and on "Check your answers": by what was
 * loaded; both types (or nothing yet) → by the goal, until a word for both exists (doc-type audit → text rewrite).
 */
export function uploadLabelKey(goal: Goal, loaded: Loaded): string {
  if (loaded.forms106 > 0 && loaded.payslips === 0) return 'flow.summary.upload.form106';
  if (loaded.payslips > 0 && loaded.forms106 === 0) return 'flow.summary.upload';
  return goal === 'form106' ? 'flow.summary.upload.form106' : 'flow.summary.upload';
}

/**
 * Whether an answer asked under one result is asked only once on the page ("Answered in the first box above"). Not the
 * Form 106 questions that only lead to the questions step: their other income is asked per tax year, so each year's
 * result keeps its own way there.
 */
export function askedOncePerPage(key: string): boolean {
  return key !== 'incomeSources' && key !== 'taxStatuses';
}

/** Answers whose question is drawn only once on the page, even on the same payslip or Form 106 block. */
const ONCE_ON_PAGE: ReadonlySet<string> = new Set(['niCertificates']);

/** Answers asked under the results this render: by key, the payslip (or Form 106 block) and the box it is asked in. */
export type AskedOnPage = Map<string, { scope: string; box: string }>;

/**
 * Where an answer a result's fill-in box needs is asked (persona walk 2026-10-23: never an empty box): in this box when
 * it's the first on the page, or on the same payslip as the first (`ask`); under a later payslip, a link to the first
 * box (`pointTo`, that box's id). Records the first box.
 */
export function planAsk(asked: AskedOnPage, key: string, scope: string, box: string): { ask: true } | { pointTo: string } {
  const first = asked.get(key);
  // The certificate's entry draws every year's boxes, with ids of their own (src/form106/niEntry.ts): drawn once only.
  if (first && first.box !== box && (first.scope !== scope || ONCE_ON_PAGE.has(key))) return { pointTo: first.box };
  if (!first && askedOncePerPage(key)) asked.set(key, { scope, box });
  return { ask: true };
}

/**
 * How many payslips a file gave, for the data-loss dialog ("2 payslips and 1 Form 106"): its digital payslips, or its
 * scan's pages being reviewed (their numbers may have been corrected). A scan that failed (not a Form 106, nothing read)
 * gave no payslip, whatever pages OCR went through.
 */
export function payslipsOfFile(digital: number, scan: { status: string; pages: number } | undefined): number {
  return Math.max(digital, scan?.status === 'review' ? scan.pages : 0);
}

/** A file the diagnostic report counts as recognised: payslips read, or a Form 106 (never "unknown layout"). */
export function fileRecognised(u: { payslips: readonly unknown[]; form106?: unknown }): boolean {
  return u.payslips.length > 0 || u.form106 !== undefined;
}

/**
 * The questions step's one-line summary, its Form 106 part: "your Form 106 answers" only with a Form 106 loaded. Sex is
 * also asked for payslips (the pension age): without a Form 106 it shows as itself ("Man"), never as a Form 106 answer.
 */
export function answersSummaryParts(a: { incomeSources?: object; taxStatuses?: unknown; sex?: string }, form106: boolean): string[] {
  if (form106) return Object.keys(a.incomeSources ?? {}).length || a.taxStatuses !== undefined || a.sex ? ['flow.summary.form106Answers'] : [];
  return a.sex ? [`questions.sex.${a.sex}`] : [];
}
